import ga = require("ga");
import * as React from "react";
import * as Stores from "client/stores";
import * as Frontend from "kmmp/frontend";
import { useLocation } from "wouter";
import { useState } from "client/hooks";
import { Paths } from "shared/paths";
import PageHeader from "client/components/page-header";
import { uuid } from "shared/uuid";
import { Auth } from "client/stores";
import { Option } from "@nozzlegear/railway";
import { VisualBoxSelector } from "client/pages/quick/visual_box_selector";
import { QuantitySelector } from "./quantity_selector";
import { PurchaseOrderIdBox } from "./purchase_order_id_box";
import { purchaseOrderIdIsValid } from "shared/purchase-order-id-is-valid";
import { ShadowBoxColor } from "kmmp/frontend";
import { EngravingControls } from "client/pages/quick/engraving_controls";
import { ImageUploadDialog } from "client/pages/quick/image_upload_dialog";
import { Warning } from "client/components/warning";

type Props = React.ClassAttributes<{}>;

interface State {
    box: ShadowBoxColor;
    engravingName: string;
    engravingDate1: string;
    engravingDate2: string;
    engravingSentiment: string;
    purchaseOrderId: string;
    quantity: number;
    showUploadForm: boolean;
    error: Option<string>;
}

export function ShadowBoxesPage(props: Props): JSX.Element {
    const [_, setLocation] = useLocation();
    const [state, setState] = useState<State>({
        box: "Mahogany",
        engravingName: "",
        engravingDate1: "",
        engravingDate2: "",
        engravingSentiment: "",
        purchaseOrderId: "",
        quantity: 1,
        showUploadForm: false,
        error: Option.ofNone(),
    });
    const isMobile = window.innerWidth < 767;

    const setPurchaseOrderId: (value: string) => void = (value) => {
        setState({ purchaseOrderId: value });
    };

    const setBox: (value: ShadowBoxColor) => void = (value) => {
        setState({ box: value });
    };

    const setQuantity: (value: number) => void = (value) => {
        setState({ quantity: value });
    };

    const setEngravingName: (value: string) => void = (value) => {
        setState({ engravingName: value });
    };

    const setEngravingDate1: (value: string) => void = (value) => {
        setState({ engravingDate1: value });
    };

    const setEngravingDate2: (value: string) => void = (value) => {
        setState({ engravingDate2: value });
    };

    const setEngravingSentiment: (value: string) => void = (value) => {
        setState({ engravingSentiment: value });
    };

    const closeUploadForm = () =>
        setState({
            showUploadForm: false,
        });

    const openUploadForm = () => {
        if (state.quantity < 1 || state.quantity > 10) {
            setState({
                error: Option.ofSome("You must select a quantity between 1 and 10."),
            });
            return;
        }

        if (Stores.Auth.requireCoupaPurchaseOrder && !state.purchaseOrderId) {
            setState({
                error: Option.ofSome(
                    "Your location is part of our HMIS+ integration. Please enter a valid Purchase Order ID from HMIS+."
                ),
            });
            return;
        }

        if (Stores.Auth.requireCoupaPurchaseOrder && !purchaseOrderIdIsValid(state.purchaseOrderId)) {
            setState({
                error: Option.ofSome("You must enter a valid Purchase Order ID."),
            });
            return;
        }

        setState({
            showUploadForm: true,
            error: Option.ofNone(),
        });
    };

    const addToCart = async (uploadedImages: Frontend.UploadedImage[]) => {
        Stores.Cart.addToCart({
            uuid: uuid(),
            type: "shadow-box",
            customization: {
                package: "SB1",
                color: state.box,
                engravingName: state.engravingName || null,
                engravingDate1: state.engravingDate1 || null,
                engravingDate2: state.engravingDate2 || null,
                engravingSentiment: state.engravingSentiment || null,
            },
            qty: {
                packages: state.quantity,
            },
            images: uploadedImages,
            source: "quick",
            purchaseOrderId: state.purchaseOrderId,
            deceasedName: "",
            originalOrderId: null,
        });

        if (uploadedImages.some((i) => i.tooSmall)) {
            ga("send", "event", {
                eventCategory: "Image",
                eventAction: "ignored-too-small",
            });
        }

        setLocation(Stores.Auth.companySettings.siteType === "ecommerce" ? Paths.cart.index : Paths.cart.name);
    };

    return (
        <section id="cart">
            <PageHeader title={`Add Prestige Shadow Box to Cart`} />
            <div className="section white pure-g center-children">
                <div className="pure-u-22-24">
                    <div className="pure-u-13-24 col-sm-pull-5">
                        <VisualBoxSelector
                            whitelabelSettings={Auth.companySettings}
                            color={state.box}
                            onSelectColor={setBox}
                            displayAsPackage={false}
                        >
                            <Warning title="Personalization charges">
                                <ul>
                                    <li>
                                        <strong>{"$10.00"}</strong>
                                        {" - Engraving plexiglass with Sentiment, Name and Dates"}
                                    </li>
                                    <li>
                                        <strong>{"$15.00"}</strong>
                                        {" - Adding a photograph"}
                                    </li>
                                </ul>
                                <p>
                                    <strong>{"Note:"}</strong>
                                    {
                                        " items to be placed in the shadow box are not supplied by the Keith Merrick Company. Images shown with items in the shadow box are for example only."
                                    }
                                </p>
                            </Warning>
                        </VisualBoxSelector>
                    </div>
                    <div className="pure-u-2-24" />
                    <div className="pure-u-9-24 col-sm-push-7">
                        <div className="product main" itemScope={true} itemType="http://schema.org/Product">
                            <h1 className="heading" itemProp="name">
                                {"Prestige Shadow Box"}
                            </h1>
                            <hr style={{ margin: "25px 0" }} />
                            <Controls
                                box={state.box}
                                quantity={state.quantity}
                                purchaseOrderId={state.purchaseOrderId}
                                engravingName={state.engravingName}
                                engravingDate1={state.engravingDate1}
                                engravingDate2={state.engravingDate2}
                                engravingSentiment={state.engravingSentiment}
                                error={state.error}
                                onSelectBox={setBox}
                                onOpenUploadForm={openUploadForm}
                                onSetQuantity={setQuantity}
                                onSetPurchaseOrderId={setPurchaseOrderId}
                                onSetEngravingName={setEngravingName}
                                onSetEngravingDate1={setEngravingDate1}
                                onSetEngravingDate2={setEngravingDate2}
                                onSetEngravingSentiment={setEngravingSentiment}
                            />
                            <ImageUploadDialog
                                isOpen={state.showUploadForm}
                                onClose={closeUploadForm}
                                orientation={"landscape"}
                                onImagesUploaded={addToCart}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

interface ControlsProps extends React.Props<any> {
    box: ShadowBoxColor;
    purchaseOrderId: string;
    onSelectBox: (color: ShadowBoxColor) => void;
    onSetPurchaseOrderId: (id: string) => void;
    onOpenUploadForm: () => void;
    quantity: number;
    onSetQuantity: (newValue: number) => void;
    engravingName: string;
    onSetEngravingName: (newValue: string) => void;
    engravingDate1: string;
    onSetEngravingDate1: (newValue: string) => void;
    engravingDate2: string;
    onSetEngravingDate2: (newValue: string) => void;
    engravingSentiment: string;
    onSetEngravingSentiment: (newValue: string) => void;
    error: Option<string>;
}

function Controls(props: ControlsProps): JSX.Element {
    const colors: ShadowBoxColor[] = ["Mahogany", "White"];
    const onOpenUploadForm = (e: React.MouseEvent<any>) => {
        e.preventDefault();
        props.onOpenUploadForm();
    };

    return (
        <div key={"all-controls"}>
            <div key={"generic-form-controls"}>
                {Stores.Auth.requireCoupaPurchaseOrder ? (
                    <PurchaseOrderIdBox value={props.purchaseOrderId} onChange={props.onSetPurchaseOrderId} />
                ) : null}
                <div className="selector-container">
                    <p>{"Box Color"}</p>
                    <select
                        className="form-control selector"
                        value={props.box}
                        onChange={(e) => props.onSelectBox(e.currentTarget.value as ShadowBoxColor)}
                    >
                        {colors.map((color) => (
                            <option key={color} value={color}>
                                {color}
                            </option>
                        ))}
                    </select>
                </div>
                <QuantitySelector
                    min={1}
                    max={10}
                    quantity={props.quantity}
                    onChange={props.onSetQuantity}
                    leftLabel={"Quantity"}
                    rightLabel={Option.ofNone()}
                />

                <EngravingControls
                    forSignaturePortraits={false}
                    name={props.engravingName}
                    date1={props.engravingDate1}
                    date2={props.engravingDate2}
                    sentiment={props.engravingSentiment}
                    onNameChanged={props.onSetEngravingName}
                    onDate1Changed={props.onSetEngravingDate1}
                    onDate2Changed={props.onSetEngravingDate2}
                    onSentimentChanged={props.onSetEngravingSentiment}
                />

                <div className="selector-container">
                    {props.error
                        .map((str) => <p className="error text-center padTop0">{str}</p>)
                        .defaultWith(() => (
                            <p>{String.fromCharCode(160)}</p>
                        ))}
                    <div className="add-to-cart">
                        <button type="button" className="btn blue" onClick={onOpenUploadForm}>
                            {"Select Prestige Shadow Box Images"}
                        </button>
                    </div>
                </div>
            </div>
            <hr className="marBottom25" />
        </div>
    );
}
